import { IconButton, Stack, Typography, useTheme } from "@mui/material";
import { CaretDoubleLeftIcon } from "@phosphor-icons/react/dist/csr/CaretDoubleLeft";
import { ChartBarIcon } from "@phosphor-icons/react/dist/csr/ChartBar";
import { ClockCounterClockwiseIcon } from "@phosphor-icons/react/dist/csr/ClockCounterClockwise";
import { CloudArrowDownIcon } from "@phosphor-icons/react/dist/csr/CloudArrowDown";
import { CloudArrowUpIcon } from "@phosphor-icons/react/dist/csr/CloudArrowUp";
import { EnvelopeSimpleIcon } from "@phosphor-icons/react/dist/csr/EnvelopeSimple";
import { FileArrowDownIcon } from "@phosphor-icons/react/dist/csr/FileArrowDown";
import { FileArrowUpIcon } from "@phosphor-icons/react/dist/csr/FileArrowUp";
import { FileDashedIcon } from "@phosphor-icons/react/dist/csr/FileDashed";
import { FlaskIcon } from "@phosphor-icons/react/dist/csr/Flask";
import { FlowArrowIcon } from "@phosphor-icons/react/dist/csr/FlowArrow";
import { HouseIcon } from "@phosphor-icons/react/dist/csr/House";
import { KeyIcon } from "@phosphor-icons/react/dist/csr/Key";
import { ListMagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/ListMagnifyingGlass";
import { NotepadIcon } from "@phosphor-icons/react/dist/csr/Notepad";
import { PlugIcon } from "@phosphor-icons/react/dist/csr/Plug";
import { StarIcon } from "@phosphor-icons/react/dist/csr/Star";

import { useLocation, useNavigate } from "react-router-dom";
import { JSX } from "react/jsx-runtime";
import Permissions from "../../constants/permissions";
import { useAccountContext } from "../../strict/app/AccountResolver";
import NotepadDownload from "../../icons/NotepadDownload.svg?react";
import NotepadUpload from "../../icons/NotepadUpload.svg?react";
import SbLightLogo from "../../icons/SbLogoLight.svg?react";
import ScriptIcon from "../../icons/Script.svg?react";
import { CircleWithCount } from "../IconForFilter/IconForFilter";
import { IfPermission } from "../Permissions";

const NavItem = ({
  name,
  icon,
  paddingLeft = "40px",
  paddingRight = "0px",
  width = "239px",
  height = "44px",
  isSubItem = false,
  selectedIfUrlIs,
  navigateTo,
  errorCount = 0,
}: {
  name: string;
  icon: JSX.Element;
  paddingLeft?: string;
  paddingRight?: string;
  width?: string;
  height?: string;
  isSubItem?: boolean;
  selectedIfUrlIs?: string[];
  navigateTo?: string;
  errorCount?: number;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const selected = selectedIfUrlIs?.some((url) => location.pathname.includes(url)) || navigateTo === location.pathname;
  return (
    <Stack
      sx={(theme) => ({
        "&:hover": {
          cursor: "pointer",
          backgroundColor: theme.palette.surfaceLight30.main,
        },
        alignItems: "center",
        paddingLeft,
        paddingRight,
        gap: "8px",
        backgroundColor: selected ? theme.palette.surfaceLight30.main : undefined,
        borderRadius: width === "auto" ? "0" : "0 8px 8px 0",
        ...(isSubItem && {
          borderLeft: `1px solid ${theme.palette.surfaceLight50.main}`,
        }),
        width,
        minHeight: height,
      })}
      onClick={() => {
        if (navigateTo) {
          navigate(navigateTo);
        }
      }}
      data-testid={`nav-item-${name}`}
      direction="row"
    >
      {icon}
      <Typography variant="heading.seven" sx={{ color: "text40.main" }}>
        {name}
      </Typography>
      {errorCount > 0 && (
        <Stack data-testid="suspended-keys">
          <CircleWithCount count={errorCount} />
        </Stack>
      )}
    </Stack>
  );
};

const SubItemsTitle = ({ name, icon, textColor }: { name: string; icon: JSX.Element; textColor?: string }) => {
  return (
    <Stack direction="row" sx={{ alignItems: "center", gap: "8px", height: "44px" }}>
      {icon}
      <Typography variant="heading.tag" style={{ color: textColor || "" }}>
        {name}
      </Typography>
    </Stack>
  );
};

const ItemWithSubItems = ({ children }: { children: JSX.Element[] }) => {
  return (
    <Stack direction="column" sx={{ paddingLeft: "40px" }}>
      {children}
    </Stack>
  );
};

const SubItemsContainer = ({ children }: { children: JSX.Element[] }) => {
  return (
    <Stack direction="column" sx={{ paddingLeft: "12px" }}>
      {children}
    </Stack>
  );
};

const subItemsProps = {
  paddingLeft: "19px",
  width: "187px",
  isSubItem: true,
};

const ExpandedNavBar = ({
  toggleMiniNavbar,
  logoHeight,
  toggleNavbarHeight,
  suspendedKeysCount,
}: {
  toggleMiniNavbar: () => void;
  logoHeight: string;
  toggleNavbarHeight: string;
  suspendedKeysCount: number;
}) => {
  const { urlName } = useAccountContext();
  const urlPrefix = `/app/${urlName}`;
  const theme = useTheme();
  return (
    <>
      <Stack sx={{ width: "263px", height: logoHeight, alignItems: "center", justifyContent: "center" }}>
        <SbLightLogo width="183px" height="46px" data-testid="sb-light-logo-large" fill={theme.palette.text50.main} />
      </Stack>{" "}
      <Stack
        sx={{
          height: `calc(99vh - ${logoHeight} - ${toggleNavbarHeight})`,
          overflowY: "hidden",
          "&:hover": {
            overflowY: "auto",
          },
          gap: "8px",
        }}
      >
        <NavItem
          navigateTo={`${urlPrefix}/dashboard`}
          selectedIfUrlIs={[`${urlPrefix}/dashboard`, "homepage/charts"]}
          name="Home"
          icon={<HouseIcon size="24px" color={theme.palette.text40.main} />}
        />

        <IfPermission permission={Permissions.Backfill}>
          <NavItem
            navigateTo={`${urlPrefix}/backfills/list`}
            selectedIfUrlIs={["/backfills"]}
            name="Backfills"
            icon={<FlowArrowIcon size="24px" color={theme.palette.text40.main} />}
          />
        </IfPermission>
        <ItemWithSubItems>
          <SubItemsTitle
            name="Downloads"
            icon={<CloudArrowDownIcon size="24px" color={theme.palette.text40.main} />}
            textColor={theme.palette.text40.main}
          />
          <SubItemsContainer>
            <NavItem
              navigateTo={`${urlPrefix}/downloads/list`}
              name="Files"
              icon={<FileArrowDownIcon size="24px" color={theme.palette.text40.main} />}
              {...subItemsProps}
            />
            <NavItem
              navigateTo={`${urlPrefix}/imports/dashboard`}
              selectedIfUrlIs={["imports/dashboard"]}
              name="Charts"
              icon={<ChartBarIcon size="24px" color={theme.palette.text40.main} />}
              {...subItemsProps}
            />
            <IfPermission permission={Permissions.EmailIngestV3}>
              <NavItem
                navigateTo={`${urlPrefix}/email-ingest`}
                name="Email Ingest"
                icon={<EnvelopeSimpleIcon size="24px" color={theme.palette.text40.main} />}
                {...subItemsProps}
              />
            </IfPermission>
          </SubItemsContainer>
        </ItemWithSubItems>
        <ItemWithSubItems>
          <SubItemsTitle
            name="Uploads"
            icon={<CloudArrowUpIcon size="24px" color={theme.palette.text40.main} />}
            textColor={theme.palette.text40.main}
          />
          <SubItemsContainer>
            <NavItem
              navigateTo={`${urlPrefix}/uploads/list`}
              name="Files"
              icon={<FileArrowUpIcon size="24px" color={theme.palette.text40.main} />}
              {...subItemsProps}
            />
            <NavItem
              navigateTo={`${urlPrefix}/exports/dashboard`}
              selectedIfUrlIs={["exports/dashboard"]}
              name="Charts"
              icon={<ChartBarIcon size="24px" color={theme.palette.text40.main} />}
              {...subItemsProps}
            />
          </SubItemsContainer>
        </ItemWithSubItems>
        <NavItem
          name="Events"
          icon={<ListMagnifyingGlassIcon size="24px" color={theme.palette.text40.main} />}
          navigateTo={`${urlPrefix}/events`}
        />
        <NavItem
          name="Keys"
          icon={<KeyIcon size="24px" color={theme.palette.text40.main} />}
          navigateTo={`${urlPrefix}/keys`}
          errorCount={suspendedKeysCount}
          selectedIfUrlIs={["keys", "keys/details"]}
        />
        <ItemWithSubItems>
          <SubItemsTitle
            name="Scripts"
            icon={<ScriptIcon height="24px" width="24px" color={theme.palette.text40.main} />}
            textColor={theme.palette.text40.main}
          />
          <SubItemsContainer>
            <NavItem
              navigateTo={`${urlPrefix}/scripts/published`}
              name="Published"
              icon={<StarIcon size="24px" color={theme.palette.text40.main} />}
              {...subItemsProps}
            />
            <NavItem
              navigateTo={`${urlPrefix}/scripts/drafts`}
              name="Drafts"
              icon={<FileDashedIcon size="24px" color={theme.palette.text40.main} />}
              {...subItemsProps}
            />
            <NavItem
              navigateTo={`${urlPrefix}/scripts/tests`}
              name="Tests"
              icon={<FlaskIcon size="24px" color={theme.palette.text40.main} />}
              {...subItemsProps}
            />
            <NavItem
              navigateTo={`${urlPrefix}/scripts/history`}
              name="History"
              icon={<ClockCounterClockwiseIcon size="24px" color={theme.palette.text40.main} />}
              {...subItemsProps}
            />
          </SubItemsContainer>
        </ItemWithSubItems>
        <NavItem
          navigateTo={`${urlPrefix}/integrations`}
          name="Integrations"
          icon={<PlugIcon width="24px" height="24px" color={theme.palette.text40.main} />}
        />
        <IfPermission permission={Permissions.ListAccounts}>
          <ItemWithSubItems>
            <SubItemsTitle
              name="Reports"
              icon={<NotepadIcon width="24px" height="24px" color={theme.palette.text40.main} />}
              textColor={theme.palette.text40.main}
            />
            <SubItemsContainer>
              <NavItem
                navigateTo={`${urlPrefix}/reports/source`}
                name="Source"
                icon={<NotepadDownload width="24px" height="24px" color={theme.palette.text40.main} />}
                {...subItemsProps}
              />
              <NavItem
                navigateTo={`${urlPrefix}/reports/schema`}
                name="Schema"
                icon={<NotepadUpload width="24px" height="24px" color={theme.palette.text40.main} />}
                {...subItemsProps}
              />
            </SubItemsContainer>
          </ItemWithSubItems>
        </IfPermission>
      </Stack>
      <Stack
        direction="row"
        sx={{ height: toggleNavbarHeight, justifyContent: "flex-end", alignItems: "center", padding: "16px" }}
      >
        <IconButton onClick={toggleMiniNavbar} data-testid="navbar-toggle">
          <CaretDoubleLeftIcon size="24px" data-testid="close-navbar" color={theme.palette.text40.main} />
        </IconButton>
      </Stack>
    </>
  );
};

export { ExpandedNavBar, NavItem };
